import posthog from 'posthog-js';

// Environment detection
const isDevelopment = import.meta.env.DEV;

// PostHog configuration
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_API_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

// Flag to check if PostHog is initialized
let isInitialized = false;

/**
 * Initialize PostHog analytics
 * Should be called as early as possible in the application lifecycle
 */
export function initializeAnalytics(): void {
  // Only initialize if API key is provided
  if (!POSTHOG_KEY) {
    if (isDevelopment) {
      console.log('[PostHog] API key not provided, skipping initialization');
    }
    return;
  }

  // Prevent double initialization
  if (isInitialized) {
    console.warn('[PostHog] Already initialized');
    return;
  }

  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,

      // Privacy settings
      mask_all_text: false, // Set to true if you want to mask all text
      mask_all_element_attributes: false,

      // Capture settings
      autocapture: true, // Automatically capture clicks, form submits, etc.
      capture_pageview: true, // Automatically capture page views
      capture_pageleave: true, // Capture when users leave pages

      // Session recording (disabled by default for privacy)
      disable_session_recording: !import.meta.env.VITE_POSTHOG_ENABLE_RECORDINGS,

      // Cross-domain tracking (if needed)
      cross_subdomain_cookie: false,

      // Persistence
      persistence: 'localStorage+cookie',

      // Advanced settings
      loaded: (posthog) => {
        if (isDevelopment) {
          posthog.debug(!!import.meta.env.VITE_POSTHOG_DEBUG);
          console.log('[PostHog] Initialized successfully');
        }
      },

      // Bootstrap feature flags (optional - for faster initial load)
      bootstrap: {
        featureFlags: {},
      },

      // Performance
      sanitize_properties: (properties) => {
        // Remove sensitive data from properties
        const sanitized = { ...properties };
        delete sanitized.password;
        delete sanitized.credit_card;
        delete sanitized.ssn;
        delete sanitized.api_key;
        delete sanitized.token;
        return sanitized;
      },
    });

    isInitialized = true;
  } catch (error) {
    console.error('[PostHog] Initialization failed:', error);
  }
}

/**
 * Check if PostHog is initialized and ready
 */
export function isAnalyticsReady(): boolean {
  return isInitialized && !!POSTHOG_KEY;
}

/**
 * Identify a user in PostHog
 * Call this after user authentication
 */
export function identifyUser(
  userId: string,
  properties?: {
    email?: string;
    role?: string;
    username?: string;
    signupDate?: string;
    creditsBalance?: number;
    applicationsCount?: number;
    jobsPostedCount?: number;
    [key: string]: string | number | boolean | undefined;
  }
): void {
  if (!isAnalyticsReady()) return;

  try {
    posthog.identify(userId, {
      ...properties,
      // Add timestamps
      last_seen: new Date().toISOString(),
    });

    if (isDevelopment) {
      console.log('[PostHog] User identified:', userId, properties);
    }
  } catch (error) {
    console.error('[PostHog] Failed to identify user:', error);
  }
}

/**
 * Reset user identity (on logout)
 */
export function resetUser(): void {
  if (!isAnalyticsReady()) return;

  try {
    posthog.reset();

    if (isDevelopment) {
      console.log('[PostHog] User reset');
    }
  } catch (error) {
    console.error('[PostHog] Failed to reset user:', error);
  }
}

/**
 * Track a custom event
 */
export function trackEvent(
  eventName: string,
  properties?: Record<string, unknown>
): void {
  if (!isAnalyticsReady()) {
    if (isDevelopment) {
      console.log('[PostHog] Event tracked (not sent):', eventName, properties);
    }
    return;
  }

  try {
    posthog.capture(eventName, {
      ...properties,
      timestamp: new Date().toISOString(),
    });

    if (isDevelopment) {
      console.log('[PostHog] Event tracked:', eventName, properties);
    }
  } catch (error) {
    console.error('[PostHog] Failed to track event:', error);
  }
}

/**
 * Track a page view
 */
export function trackPageView(pageName: string, properties?: Record<string, unknown>): void {
  trackEvent('$pageview', {
    page_name: pageName,
    ...properties,
  });
}

/**
 * Set user properties
 */
export function setUserProperties(properties: Record<string, unknown>): void {
  if (!isAnalyticsReady()) return;

  try {
    posthog.setPersonProperties(properties);

    if (isDevelopment) {
      console.log('[PostHog] User properties set:', properties);
    }
  } catch (error) {
    console.error('[PostHog] Failed to set user properties:', error);
  }
}

/**
 * Track a conversion event (for goal tracking)
 */
export function trackConversion(
  conversionName: string,
  value?: number,
  properties?: Record<string, unknown>
): void {
  trackEvent(`conversion_${conversionName}`, {
    conversion_value: value,
    ...properties,
  });
}

// Predefined event tracking functions for common actions

/**
 * Track user signup
 */
export function trackSignup(role: 'job_seeker' | 'employer', properties?: Record<string, unknown>): void {
  trackEvent('user_signup', {
    role,
    ...properties,
  });
}

/**
 * Track job swipe
 */
export function trackSwipe(
  direction: 'left' | 'right',
  jobId: string,
  properties?: Record<string, unknown>
): void {
  trackEvent('job_swipe', {
    direction,
    job_id: jobId,
    swipe_type: direction === 'right' ? 'interested' : 'not_interested',
    ...properties,
  });
}

/**
 * Track application submission
 */
export function trackApplication(
  jobId: string,
  properties?: Record<string, unknown>
): void {
  trackEvent('application_submitted', {
    job_id: jobId,
    ...properties,
  });
}

/**
 * Track resume upload
 */
export function trackResumeUpload(
  resumeId: string,
  properties?: Record<string, unknown>
): void {
  trackEvent('resume_uploaded', {
    resume_id: resumeId,
    ...properties,
  });
}

/**
 * Track credits purchase
 */
export function trackCreditsPurchase(
  amount: number,
  credits: number,
  properties?: Record<string, unknown>
): void {
  trackEvent('credits_purchased', {
    amount,
    credits,
    ...properties,
  });

  // Also track as a conversion
  trackConversion('credits_purchase', amount, { credits });
}

/**
 * Track referral code usage
 */
export function trackReferralUsed(
  referralCode: string,
  properties?: Record<string, unknown>
): void {
  trackEvent('referral_code_used', {
    referral_code: referralCode,
    ...properties,
  });
}

/**
 * Track profile completion
 */
export function trackProfileCompleted(
  completionPercentage: number,
  properties?: Record<string, unknown>
): void {
  trackEvent('profile_completed', {
    completion_percentage: completionPercentage,
    ...properties,
  });

  // Track as conversion if 100% complete
  if (completionPercentage === 100) {
    trackConversion('profile_complete');
  }
}

/**
 * Track feature usage
 */
export function trackFeatureUsage(
  featureName: string,
  properties?: Record<string, unknown>
): void {
  trackEvent('feature_used', {
    feature_name: featureName,
    ...properties,
  });
}

/**
 * Track job posting
 */
export function trackJobPosted(
  jobId: string,
  properties?: Record<string, unknown>
): void {
  trackEvent('job_posted', {
    job_id: jobId,
    ...properties,
  });
}

/**
 * Track message sent
 */
export function trackMessageSent(
  recipientId: string,
  properties?: Record<string, unknown>
): void {
  trackEvent('message_sent', {
    recipient_id: recipientId,
    ...properties,
  });
}

/**
 * Get feature flag value
 */
export function getFeatureFlag(flagName: string, defaultValue: boolean = false): boolean {
  if (!isAnalyticsReady()) return defaultValue;

  try {
    const value = posthog.isFeatureEnabled(flagName);
    return value !== undefined ? value : defaultValue;
  } catch (error) {
    console.error('[PostHog] Failed to get feature flag:', error);
    return defaultValue;
  }
}

/**
 * Get feature flag with payload
 */
export function getFeatureFlagPayload<T = unknown>(
  flagName: string,
  defaultValue?: T
): T | undefined {
  if (!isAnalyticsReady()) return defaultValue;

  try {
    const payload = posthog.getFeatureFlagPayload(flagName);
    return (payload as T) || defaultValue;
  } catch (error) {
    console.error('[PostHog] Failed to get feature flag payload:', error);
    return defaultValue;
  }
}

/**
 * Reload feature flags (use sparingly)
 */
export function reloadFeatureFlags(): Promise<void> {
  if (!isAnalyticsReady()) return Promise.resolve();

  return new Promise((resolve) => {
    try {
      posthog.reloadFeatureFlags();
      // Wait a bit for flags to load
      setTimeout(resolve, 100);
    } catch (error) {
      console.error('[PostHog] Failed to reload feature flags:', error);
      resolve();
    }
  });
}

// Export PostHog instance for advanced usage
export { posthog };
